// A term line, written as its five clauses.
//
// The form is GROUPED BY CLAUSE and says so, because that is what a term line
// is: a selector (which facts), a quantity (how many units), a price (what a
// unit is worth), a share (whose money that is) and a period (which invoice it
// lands on). A flat form of eighteen fields would hide the one thing a reader
// has to hold — that these five answers together are the whole rule.
//
// THE EXPRESSION FIELDS TAKE JSON. The selector and the quantity are the
// contract's expression language, and it has no half-built visual editor: a
// rule that bills money should not be authored through a widget nobody has
// tested against the evaluator. What the form does instead is refuse a shape
// the contract refuses — with `termLineShapeProblem`, the same rule the server
// applies — and show the server's own message beside the field it names.
//
// TWO CLAUSES THAT MATCH ONE FACT ARE REFUSED, and `stacks_with` is how a
// person overrules that. Silent double billing is the worst failure this system
// could have, so a write that would leave two active clauses on one deal whose
// selectors both match one fact comes back 422 — unless one of them names the
// other, which says "these really are two products, and one call buys both".
// The field is on the Selector clause, because that is the clause it is about.
//
// AND THERE IS A "TRY IT" BUTTON, which is the point of the whole screen.
// `previewDealStatement` runs the form's current values over a period and
// writes nothing, so the statement line this rule WOULD produce appears beside
// the saved ones before anybody agrees to it. An interpretation somebody has
// seen is a different thing from one they have described.

import { useCallback, useState, type ReactNode } from "react";
import {
  BillingTiming,
  Cadence,
  Collection,
  MeterExpr,
  MeterSource,
  MeterUnit,
  PriceSource,
  ShareKind,
  TermLineCreate,
  TermLineMilestone,
  TermLinePreview,
  TermLineUpdate,
  termLineShapeProblem,
  type Currency,
  type DealStatementLine,
  type PreviewDealStatementRequest,
  type TermLine,
} from "@crm/shared";
import styles from "./TermLineFormDialog.module.css";
import { usePreviewDealStatementMutation, useTermLineMutations } from "../../api/mutations.js";
import { useTermLines } from "../../api/queries.js";
import { describeError } from "../../api/problem.js";
import { Button } from "../../design/Button.js";
import { Notice } from "../../design/Feedback.js";
import { FormDialog } from "../../forms/FormDialog.js";
import {
  CheckboxField,
  DateField,
  NumberField,
  SelectField,
  TextField,
} from "../../forms/fields.js";
import { optionalDate, optionalText, parseOptionalDecimal } from "../../forms/parseInput.js";
import { minorToInput, parseMoneyToMinor } from "../../format/money.js";
import { useEditor, type Wire } from "../../forms/useEditor.js";
import type { FieldErrors } from "../../forms/formErrors.js";
import { termLineSentence } from "./termLineSentence.js";

interface TermLineDraft {
  label: string;
  kind: string;
  source: string;
  where: string;
  quantity: string;
  unit: string;
  price_source: string;
  rate: string;
  amount: string;
  share_kind: string;
  share_override_pct: string;
  cadence: string;
  billing_timing: string;
  collection: string;
  effective_from: string;
  effective_to: string;
  milestones: string;
  stacksWith: string;
  note: string;
  active: boolean;
}

const BLANK_DRAFT: TermLineDraft = {
  label: "",
  kind: "revenue",
  source: "transcripts",
  where: "",
  quantity: "",
  unit: "call",
  price_source: "contract_rate",
  rate: "",
  amount: "",
  share_kind: "recurring",
  share_override_pct: "",
  cadence: "monthly",
  billing_timing: "arrears",
  collection: "we_invoice",
  effective_from: "",
  effective_to: "",
  milestones: "",
  stacksWith: "",
  note: "",
  active: true,
};

const LABELS = {
  label: "Name",
  kind: "Kind",
  source: "Reads",
  where: "Selector",
  quantity: "Quantity",
  unit: "Unit",
  price_source: "Price source",
  rate_minor: "Rate",
  amount_minor: "Amount",
  share_kind: "Share",
  share_override_pct: "Share override",
  cadence: "Cadence",
  billing_timing: "Timing",
  collection: "Collection",
  effective_from: "In force from",
  effective_to: "In force to",
  milestones: "Milestones",
  stacks_with: "Stacks with",
  note: "Note",
  active: "Active",
} as const;

const KIND_OPTIONS = [
  { value: "revenue" as const, label: "Revenue — we sell it" },
  {
    value: "pass_through_cost" as const,
    label: "Pass-through cost — we spent it, and recharge a share",
  },
];

const SOURCE_OPTIONS = MeterSource.options.map((source) => ({ value: source, label: source }));
const UNIT_OPTIONS = MeterUnit.options.map((unit) => ({ value: unit, label: unit }));
const PRICE_OPTIONS = PriceSource.options.map((price) => ({ value: price, label: price }));
const SHARE_OPTIONS = ShareKind.options.map((kind) => ({ value: kind, label: kind }));
const CADENCE_OPTIONS = Cadence.options.map((cadence) => ({ value: cadence, label: cadence }));
const TIMING_OPTIONS = BillingTiming.options.map((timing) => ({ value: timing, label: timing }));
const COLLECTION_OPTIONS = Collection.options.map((value) => ({ value, label: value }));

interface Parsed<T> {
  value: T | null;
  problem: string | null;
}

/** Blank text is a null expression, which is what "no selector" means. */
function parseExpr(input: string): Parsed<MeterExpr> {
  const trimmed = input.trim();
  if (trimmed === "") return { value: null, problem: null };
  let raw: unknown;
  try {
    raw = JSON.parse(trimmed);
  } catch {
    return { value: null, problem: "This is not JSON. The evaluator reads an expression object." };
  }
  const parsed = MeterExpr.safeParse(raw);
  return parsed.success
    ? { value: parsed.data, problem: null }
    : {
        value: null,
        problem:
          parsed.error.issues[0]?.message ?? "This is not an expression the evaluator reads.",
      };
}

/**
 * A milestone plan, as the paper writes it.
 *
 * Each entry is checked against the contract's own `TermLineMilestone` rather
 * than against a shape restated here, so a field added to a milestone is a
 * compile-time and a runtime concern in one place.
 */
function parseMilestones(input: string): Parsed<TermLineMilestone[]> {
  const trimmed = input.trim();
  if (trimmed === "") return { value: null, problem: null };
  let raw: unknown;
  try {
    raw = JSON.parse(trimmed);
  } catch {
    return { value: null, problem: "This is not JSON. A plan is an array of milestones." };
  }
  if (!Array.isArray(raw)) {
    return { value: null, problem: "A plan is an array of milestones." };
  }
  const milestones: TermLineMilestone[] = [];
  for (const [index, entry] of raw.entries()) {
    const parsed = TermLineMilestone.safeParse(entry);
    if (!parsed.success) {
      return {
        value: null,
        problem: `Milestone ${String(index + 1)}: ${parsed.error.issues[0]?.message ?? "is not a milestone."}`,
      };
    }
    milestones.push(parsed.data);
  }
  return { value: milestones, problem: null };
}

/**
 * The five clauses, under the names the contract gives them.
 *
 * ONE reading of the draft. Four things need it — the body a write sends, the
 * shape check that runs before the write, the sentence under the form and the
 * preview — and a second reading of the same draft is how a form comes to
 * validate one line and send another.
 */
interface TermLineClauseFields {
  label: string;
  kind: string;
  source: string;
  where: MeterExpr | null;
  quantity: MeterExpr | null;
  unit: string;
  price_source: string;
  rate_minor: number | null;
  amount_minor: number | null;
  share_kind: string;
  share_override_pct: number | null;
  cadence: string;
  billing_timing: string;
  collection: string;
  effective_from: string;
  effective_to: string | null;
  milestones: TermLineMilestone[] | null;
  stacks_with: string | null;
}

function clausesOf(draft: TermLineDraft, currency: Currency): TermLineClauseFields {
  return {
    label: draft.label.trim(),
    kind: draft.kind,
    source: draft.source,
    where: parseExpr(draft.where).value,
    quantity: parseExpr(draft.quantity).value,
    unit: draft.unit,
    price_source: draft.price_source,
    rate_minor: draft.rate.trim() === "" ? null : parseMoneyToMinor(draft.rate, currency),
    amount_minor: draft.amount.trim() === "" ? null : parseMoneyToMinor(draft.amount, currency),
    share_kind: draft.share_kind,
    share_override_pct: parseOptionalDecimal(draft.share_override_pct),
    cadence: draft.cadence,
    billing_timing: draft.billing_timing,
    collection: draft.collection,
    effective_from: draft.effective_from,
    effective_to: optionalDate(draft.effective_to),
    milestones: parseMilestones(draft.milestones).value,
    stacks_with: optionalText(draft.stacksWith),
  };
}

/** The whole draft as a preview line, or null when it is not a line yet. */
function previewLine(draft: TermLineDraft, currency: Currency): TermLinePreview | null {
  const clauses = clausesOf(draft, currency);
  const parsed = TermLinePreview.safeParse({
    ...clauses,
    label: clauses.label === "" ? "This line" : clauses.label,
    // A sentence and a preview both need a window, and a draft that has not
    // been given one yet still reads: the date is what the person is about to
    // choose, not what the rule says.
    effective_from: clauses.effective_from === "" ? "2000-01-01" : clauses.effective_from,
  });
  return parsed.success ? parsed.data : null;
}

/**
 * A clause the screen already knows the shape of.
 *
 * One caller: the implementation-fee card, which has resolved what the
 * agreement says this deal's buy-in fee should be and can therefore fill in
 * four of the five clauses. It opens the SAME form rather than a second one, so
 * a prefilled line and a hand-written one cannot come to differ.
 */
export interface TermLinePrefill {
  readonly label: string;
  readonly amountMinor: number;
}

export interface TermLineFormDialogProps {
  dealId: string;
  currency: Currency;
  /** The period "Try it" runs against — the section's own period picker. */
  period: string;
  line?: TermLine;
  prefill?: TermLinePrefill;
  onClose: () => void;
  /** Hands a preview line to the section, which draws it beside the saved ones. */
  onPreview: (line: DealStatementLine | null) => void;
}

export function TermLineFormDialog({
  dealId,
  currency,
  period,
  line,
  prefill,
  onClose,
  onPreview,
}: TermLineFormDialogProps): ReactNode {
  const mutations = useTermLineMutations();
  const preview = usePreviewDealStatementMutation();
  const [previewProblem, setPreviewProblem] = useState<string | null>(null);

  /**
   * The deal's saved clauses, so "Try it" can find the previewed one.
   *
   * The section above this dialog has already asked for them, so this reads
   * the same cached answer rather than a second request.
   */
  const saved = useTermLines(dealId);

  const draftFromRecord = useCallback(
    (record: TermLine): TermLineDraft => ({
      label: record.label,
      kind: record.kind,
      source: record.source,
      where: record.where === null ? "" : JSON.stringify(record.where, null, 2),
      quantity: record.quantity === null ? "" : JSON.stringify(record.quantity, null, 2),
      unit: record.unit,
      price_source: record.price_source,
      rate: record.rate_minor === null ? "" : minorToInput(record.rate_minor, currency),
      amount: record.amount_minor === null ? "" : minorToInput(record.amount_minor, currency),
      share_kind: record.share_kind,
      share_override_pct:
        record.share_override_pct === null ? "" : String(record.share_override_pct),
      cadence: record.cadence,
      billing_timing: record.billing_timing,
      collection: record.collection,
      effective_from: record.effective_from,
      effective_to: record.effective_to ?? "",
      milestones: record.milestones === null ? "" : JSON.stringify(record.milestones, null, 2),
      stacksWith: record.stacks_with ?? "",
      note: record.note ?? "",
      active: record.active,
    }),
    [currency],
  );

  /**
   * The whole body, object clauses included.
   *
   * The three object clauses are in it BEFORE the contract judges it and
   * before the diff reads it. Held outside, a milestone plan was missing from
   * the body its own schema checked — so a milestone line could not be created
   * at all — and an edit that touched only the selector diffed to nothing and
   * closed the dialog as saved.
   */
  const toWire = useCallback(
    (draft: TermLineDraft): Wire => ({
      ...clausesOf(draft, currency),
      deal_id: dealId,
      note: optionalText(draft.note),
      active: draft.active,
    }),
    [dealId, currency],
  );

  const validateDraft = useCallback(
    (draft: TermLineDraft): FieldErrors => {
      const errors: Record<string, string> = {};
      const where = parseExpr(draft.where);
      if (where.problem !== null) errors["where"] = where.problem;
      const quantity = parseExpr(draft.quantity);
      if (quantity.problem !== null) errors["quantity"] = quantity.problem;
      const milestones = parseMilestones(draft.milestones);
      if (milestones.problem !== null) errors["milestones"] = milestones.problem;
      if (draft.label.trim() === "") errors["label"] = "Give the line a name.";
      if (draft.effective_from.trim() === "") {
        errors["effective_from"] = "Say when this clause starts to apply.";
      }
      // The same question the server asks of the resulting line, asked here so
      // the dialog says which clause disagrees with which before the request
      // goes out.
      const shape = termLineShapeProblem(clausesOf(draft, currency));
      if (shape !== null) errors[shape.field] = shape.message;
      return errors;
    },
    [currency],
  );

  const create = useCallback(
    (body: TermLineCreate, idempotencyKey: string) =>
      mutations.create.mutateAsync({ body, idempotencyKey }),
    [mutations.create],
  );

  const update = useCallback(
    (id: string, body: TermLineUpdate, version: number) =>
      mutations.update.mutateAsync({ id, body, version }),
    [mutations.update],
  );

  const editor = useEditor<TermLineDraft, TermLineCreate, TermLineUpdate, TermLine>({
    mode: line === undefined ? { kind: "create" } : { kind: "update", record: line },
    blankDraft: prefilledDraft(prefill, currency),
    draftFromRecord,
    toWire,
    createSchema: TermLineCreate,
    updateSchema: TermLineUpdate,
    validateDraft,
    wireField: wireFieldOf,
    create,
    update,
    fetchCurrent: mutations.fetchCurrent,
    onSaved: () => {
      onPreview(null);
      onClose();
    },
  });

  const shown = previewLine(editor.draft, currency);
  const sentence = shown === null ? null : termLineSentence(shown, currency);

  const tryIt = useCallback(() => {
    setPreviewProblem(null);
    const term = previewLine(editor.draft, currency);
    if (term === null || editor.draft.effective_from === "") {
      setPreviewProblem("Fill in the clauses first — a preview runs the whole rule.");
      return;
    }
    const body: PreviewDealStatementRequest = {
      period,
      term_line: term,
      // An edit previews the line IN PLACE of the one it replaces, so a person
      // sees the statement they would get rather than that statement plus a
      // second copy of the clause they are changing.
      ...(line === undefined ? {} : { replaces_term_line_id: line.id }),
    };
    const savedClauses = saved.data?.items;
    if (line === undefined && savedClauses === undefined) {
      // A new clause is found in the answer by being the one line no saved
      // clause claims. Without the saved clauses that test cannot be made, and
      // showing another clause's figure as this rule's is worse than waiting.
      setPreviewProblem("The deal's clauses are still loading. Try it again in a moment.");
      return;
    }
    const savedIds = new Set((savedClauses ?? []).map((clause) => clause.id));
    preview.mutate(
      { dealId, body },
      {
        onSuccess: (statement) => {
          // A statement holds EVERY clause of the deal, so its first line is
          // some other rule — the subscription, usually. An edit's preview
          // carries the id of the clause it replaces; a new clause is the one
          // line the deal has never saved.
          const mine =
            line === undefined
              ? statement.lines.find((shownLine) => !savedIds.has(shownLine.term_line_id))
              : statement.lines.find((shownLine) => shownLine.term_line_id === line.id);
          onPreview(mine ?? null);
          if (mine === undefined) setPreviewProblem(`This rule bills nothing in ${period}.`);
        },
        onError: (error) => setPreviewProblem(describeError(error).title),
      },
    );
  }, [preview, editor.draft, dealId, period, currency, line, onPreview, saved.data]);

  return (
    <FormDialog
      open
      onClose={onClose}
      title={line === undefined ? "New term line" : "Edit term line"}
      description="Five clauses: which facts it reads, how many units they carry, what a unit is worth, whose money that is, and which invoice it lands on."
      submitLabel={line === undefined ? "Add the line" : "Save the line"}
      editor={editor}
      labels={LABELS}
    >
      <TextField
        label={LABELS.label}
        required
        value={editor.draft.label}
        error={editor.errors["label"]}
        onChange={(value) => editor.setField("label", value)}
        hint="What this clause is called on an invoice."
      />
      <SelectField
        label={LABELS.kind}
        value={editor.draft.kind}
        options={KIND_OPTIONS}
        error={editor.errors["kind"]}
        onChange={(value) => editor.setField("kind", value)}
      />

      <Clause title="Selector" question="Which facts is this about?">
        <SelectField
          label={LABELS.source}
          value={editor.draft.source}
          options={SOURCE_OPTIONS}
          error={editor.errors["source"]}
          onChange={(value) => editor.setField("source", value)}
          hint="The fact table this line reads. It also fixes which field names the selector may use."
        />
        <TextField
          label={LABELS.where}
          multiline
          value={editor.draft.where}
          error={editor.errors["where"]}
          onChange={(value) => editor.setField("where", value)}
          placeholder='{"kind":"ne","left":{"kind":"field","name":"escalation_kind"},"right":{"kind":"str","value":"instant"}}'
          hint="An expression, as JSON. Empty takes every fact. A deal-sourced line carries none — a deal fact already names its line."
        />
        <TextField
          label={LABELS.stacks_with}
          value={editor.draft.stacksWith}
          error={editor.errors["stacks_with"]}
          onChange={(value) => editor.setField("stacksWith", value)}
          hint="The id of the other clause, where one fact really does buy two products. Leave it empty: two clauses on one deal that both match one fact are refused, because silent double billing is the worst failure this system could have."
        />
      </Clause>

      <Clause title="Quantity" question="How many units do those facts carry?">
        <TextField
          label={LABELS.quantity}
          multiline
          value={editor.draft.quantity}
          error={editor.errors["quantity"]}
          onChange={(value) => editor.setField("quantity", value)}
          placeholder='{"kind":"div","left":{"kind":"field","name":"duration_seconds"},"right":{"kind":"num","value":60}}'
          hint="A number expression over one fact, as JSON. Empty means one unit per period."
        />
        <SelectField
          label={LABELS.unit}
          value={editor.draft.unit}
          options={UNIT_OPTIONS}
          error={editor.errors["unit"]}
          onChange={(value) => editor.setField("unit", value)}
        />
      </Clause>

      <Clause title="Price" question="What is one unit worth?">
        <SelectField
          label={LABELS.price_source}
          value={editor.draft.price_source}
          options={PRICE_OPTIONS}
          error={editor.errors["price_source"]}
          onChange={(value) => editor.setField("price_source", value)}
          hint="The paper states a rate, the calendar states a flat amount, or the facts state the money."
        />
        <NumberField
          label={LABELS.rate_minor}
          suffix={currency}
          value={editor.draft.rate}
          error={editor.errors["rate_minor"]}
          onChange={(value) => editor.setField("rate", value)}
          hint="What one unit costs. A contract-rate line carries this and no amount."
        />
        <NumberField
          label={LABELS.amount_minor}
          suffix={currency}
          value={editor.draft.amount}
          error={editor.errors["amount_minor"]}
          onChange={(value) => editor.setField("amount", value)}
          hint="One figure per period. A flat line carries this, counts the period, and has no quantity."
        />
      </Clause>

      <Clause title="Share" question="How much of that money is ours?">
        <SelectField
          label={LABELS.share_kind}
          value={editor.draft.share_kind}
          options={SHARE_OPTIONS}
          error={editor.errors["share_kind"]}
          onChange={(value) => editor.setField("share_kind", value)}
          hint="Which agreement percentage governs this line."
        />
        <NumberField
          label={LABELS.share_override_pct}
          suffix="%"
          value={editor.draft.share_override_pct}
          error={editor.errors["share_override_pct"]}
          onChange={(value) => editor.setField("share_override_pct", value)}
          hint="Beats the deal's override and the agreement term. Empty resolves up the chain."
        />
      </Clause>

      <Clause title="Period" question="Which invoice does it land on?">
        <SelectField
          label={LABELS.cadence}
          value={editor.draft.cadence}
          options={CADENCE_OPTIONS}
          error={editor.errors["cadence"]}
          onChange={(value) => editor.setField("cadence", value)}
        />
        <SelectField
          label={LABELS.billing_timing}
          value={editor.draft.billing_timing}
          options={TIMING_OPTIONS}
          error={editor.errors["billing_timing"]}
          onChange={(value) => editor.setField("billing_timing", value)}
          hint="Whether a bill covers the period it falls in or the one before."
        />
        <SelectField
          label={LABELS.collection}
          value={editor.draft.collection}
          options={COLLECTION_OPTIONS}
          error={editor.errors["collection"]}
          onChange={(value) => editor.setField("collection", value)}
          hint="Whether we raise a document at all. A partner-reported line is owed, and we invoice none of it."
        />
        <DateField
          label={LABELS.effective_from}
          required
          value={editor.draft.effective_from}
          error={editor.errors["effective_from"]}
          onChange={(value) => editor.setField("effective_from", value)}
        />
        <DateField
          label={LABELS.effective_to}
          value={editor.draft.effective_to}
          error={editor.errors["effective_to"]}
          onChange={(value) => editor.setField("effective_to", value)}
          hint="The last day it applies. Empty means it runs on."
        />
        <TextField
          label={LABELS.milestones}
          multiline
          value={editor.draft.milestones}
          error={editor.errors["milestones"]}
          onChange={(value) => editor.setField("milestones", value)}
          placeholder='[{"seq":1,"label":"Kickoff","pct":25,"planned_on":"2026-04-01"}]'
          hint="A milestone cadence carries this list, and the percentages must add to 100. Every other cadence leaves it empty."
        />
      </Clause>

      <TextField
        label={LABELS.note}
        multiline
        value={editor.draft.note}
        error={editor.errors["note"]}
        onChange={(value) => editor.setField("note", value)}
      />
      <CheckboxField
        label="This clause still applies"
        checked={editor.draft.active}
        onChange={(checked) => editor.setField("active", checked)}
        hint="A retired clause stays on the deal: an invoice that billed it pins the revision it billed."
      />

      {/* THE SENTENCE, as it is typed. The same words the section shows beside
          a saved line, so nobody has to save a rule to find out how it reads. */}
      {sentence !== null && <div className={styles.sentence}>{sentence}</div>}

      <div className={styles.tryIt}>
        <Button type="button" onClick={tryIt} disabled={preview.isPending}>
          {preview.isPending ? "Trying…" : "Try it"}
        </Button>
        <span className={styles.tryItNote}>
          {`Runs this rule over ${period} and writes nothing. The line it would bill appears beside the saved ones.`}
        </span>
      </div>
      {previewProblem !== null && (
        <Notice tone="warn" title="The preview could not run.">
          {previewProblem}
        </Notice>
      )}
    </FormDialog>
  );
}

/**
 * A flat one-time clause, filled in from a figure the agreement already
 * resolved. Every clause is stated: it counts the period, the paper says what
 * that period is worth, the implementation split governs it, and it bills once.
 */
function prefilledDraft(prefill: TermLinePrefill | undefined, currency: Currency): TermLineDraft {
  if (prefill === undefined) return BLANK_DRAFT;
  return {
    ...BLANK_DRAFT,
    label: prefill.label,
    source: "deal",
    unit: "period",
    price_source: "flat_per_period",
    amount: minorToInput(prefill.amountMinor, currency),
    share_kind: "implementation",
    cadence: "one_time",
  };
}

/** A titled group of fields, named for the clause it is. */
function Clause({
  title,
  question,
  children,
}: {
  title: string;
  question: string;
  children: ReactNode;
}): ReactNode {
  return (
    <fieldset className={styles.clause}>
      <legend className={styles.clauseTitle}>{title}</legend>
      <p className={styles.clauseQuestion}>{question}</p>
      {children}
    </fieldset>
  );
}

/**
 * The contract's name for a draft field, where the two differ.
 *
 * Three fields hold what a person types rather than what the body carries: a
 * rate and an amount are typed in major units, and `stacksWith` is one word in
 * the form and two in the document. Every message is keyed by the contract's
 * name, so this is what tells the editor which message a typed character
 * clears.
 */
function wireFieldOf(field: keyof TermLineDraft): string {
  switch (field) {
    case "rate":
      return "rate_minor";
    case "amount":
      return "amount_minor";
    case "stacksWith":
      return "stacks_with";
    default:
      return field;
  }
}
